import { useMemo, useRef, useState, type ReactNode } from "react";
import type { GridOverride } from "../../components/dataGrid/gridOverrides.tsx";
import {
  PRICE_COLUMN_TO_TYPE, PRICE_COLUMNS, calcTaxPairs, scopeFromRow, scopeKey,
  type PendingEdits, type PendingRow, type PriceScope, type PriceTaxPair,
} from "./advancedPriceShared.ts";
import { fmtPrice, scopePartsFromRaw, EPS, type ScopeMaps, type ScopePart } from "../../util/pricingCascade.ts";
import { derivePriceTax, effectiveRate, type PriceTaxMode } from "../../util/priceTaxDerive.ts";
import styles from "./advancedPriceGridPage.module.scss";

/** The source row's ACTUAL scope, name-resolved, keeping NULL (unset, hidden) and
 *  0 (a specified dim, shown) distinct — the same rule the grid's Scope column
 *  uses. Captured at stage time so "Changes Made" reflects the stored override
 *  rather than the 0→null-coerced intent. */
function rawScopePartsFromRow(
  rowData: unknown[], colIndex: Record<string, number>, scopeMaps: ScopeMaps,
): ScopePart[] {
  const rawDim = (name: string): string | null => {
    for (const k of Object.keys(colIndex)) {
      if (k.toLowerCase() === name.toLowerCase()) {
        const v = rowData[colIndex[k]!];
        if (v == null) return null;
        const s = String(v).trim();
        return s === "" ? null : s;
      }
    }
    return null;
  };
  return scopePartsFromRaw(rawDim, scopeMaps);
}

/** Best-effort clipboard copy with an execCommand fallback for non-secure contexts. */
function copyToClipboard(text: string): void {
  if (navigator.clipboard?.writeText) {
    void navigator.clipboard.writeText(text).catch(() => {});
    return;
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  } catch {
    /* ignore */
  }
}

/** Scope cell: kind/value styled parts, single line with ellipsis. The full
 *  composed label shows on hover (title), and double-clicking copies it to the
 *  clipboard — so long, truncated scopes are still fully readable and copyable. */
function ScopeCell({ parts, full }: { parts: ScopePart[]; full: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    copyToClipboard(full);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };
  return (
    <span
      className={styles.scopeCell}
      title={`${full}\n(double-click to copy)`}
      onDoubleClick={onCopy}
    >
      {parts.map((p, i) => (
        <span key={i} className={styles.scopePart}>
          {i > 0 && <span className={styles.scopeSep}>+</span>}
          <span className={styles.scopeKind}>{p.kind}:</span>
          <span className={styles.scopeValue}>{p.value}</span>
        </span>
      ))}
      {copied && <span className={styles.scopeCopied}>copied ✓</span>}
    </span>
  );
}

/** Column the grid relabels to "Scope" — its cell is composed client-side from
 *  the row's scope siblings (classic getScopeLabel parity). */
export const SCOPE_COLUMN = "OperatorRef";

/** Parse a number / "%"-suffixed string; "" or invalid → null. */
function parseLoose(s: string): number | null {
  const t = s.trim().replace(/%$/, "");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Calc-mode editor for a single price/tax pair, rendered inside the price column
 * cell (the paired tax column is hidden by the page). The input is the net price
 * (net mode) or the gross total (gross mode); a live readout shows the derived
 * breakdown. On blur it stages BOTH the price and tax PriceTypes into pendingRef;
 * clearing the input cancels the staged pair. The page-level rate drives the math.
 */
export function CalcPriceCell(props: {
  mode: PriceTaxMode;
  rate: string;
  pricePT: number;
  taxPT: number;
  scope: PriceScope;
  curPrice: number | null;
  curTax: number | null;
  /** Original (server) values — used to detect a no-op edit (focus+blur with no
   *  real change must NOT stage the row). Omit when there's no original (Changes Made). */
  origPrice?: number | null;
  origTax?: number | null;
  /** Original input value (total in gross mode / net in net mode) + rate, captured
   *  once from the server data — reverting to these clears the staged change. */
  baselineInput?: number | null;
  baselineRate?: number | null;
  staged: boolean;
  pendingRef: { current: PendingEdits };
  onEdited: () => void;
  /** True when taxPT is a % RATE (PT4/PT8), not a flat amount: store the rate
   *  verbatim and derive the net price against it (price+rate), like the editor. */
  taxIsRate?: boolean;
  /** PendingEdits key override. Defaults to scopeKey(scope) (advanced grid); the
   *  kiosk/location tabs key per-product, so they pass their own key here. */
  keyOverride?: string;
  /** The rate is sourced from a SIBLING column (e.g. Reserve Period uses the Rental
   *  Tax Rate): show it read-only and store ONLY the price PT, never the rate PT. */
  rateReadOnly?: boolean;
  /** Actual stored scope of this row (present-including-0), stamped onto the
   *  PendingRow so "Changes Made" shows reality. */
  rawScopeParts?: ScopePart[];
}) {
  const { mode, rate, pricePT, taxPT, scope, curPrice, curTax, origPrice, origTax, baselineInput, baselineRate, staged, taxIsRate, keyOverride, rateReadOnly, pendingRef, onEdited, rawScopeParts } = props;
  // Gross input = total incl. tax. For a flat-tax pair that's price+tax; for a
  // rate-taxed column (curTax = the %), it's net × (1 + rate/100).
  const initial =
    mode === "gross"
      ? taxIsRate
        ? (curPrice != null ? curPrice * (1 + (curTax ?? 0) / 100) : null)
        : (curPrice != null && curTax != null ? curPrice + curTax : curPrice)
      : curPrice;
  const initialText = initial != null ? fmtPrice(initial) : "";
  // Per-row rate: a rate-taxed column seeds from its STORED rate (curTax); a flat-tax
  // pair seeds from the row's effective rate; otherwise the page default.
  const initialRate = (() => {
    if (taxIsRate) return curTax != null ? `${curTax}%` : rate;
    if (curPrice != null && curTax != null && curPrice !== 0) {
      const r = effectiveRate(curPrice, curTax);
      if (r != null) return `${r}%`;
    }
    return rate;
  })();
  const [text, setText] = useState(initialText);
  const [rateText, setRateText] = useState(initialRate);
  // What the cell showed on mount — to detect a no-op blur (focus + leave, or
  // re-typing the same value) so it never stages a phantom change. Captured once;
  // the cell remounts (key changes) when the staged/original values change.
  const initialTextRef = useRef(initialText);
  const initialRateRef = useRef(initialRate);

  const derived = useMemo(
    () => derivePriceTax(mode, mode === "gross" ? { total: text, rate: rateText } : { price: text, rate: rateText }),
    [mode, text, rateText],
  );

  const commit = () => {
    const key = keyOverride ?? scopeKey(scope);
    const prev = pendingRef.current.get(key);
    const clearStaged = () => {
      // rateReadOnly cells only own pricePT — taxPT (the shared rate) belongs to
      // another column, so never touch it here.
      if (prev && (pricePT in prev.prices || (!rateReadOnly && taxPT in prev.prices))) {
        delete prev.prices[pricePT];
        if (!rateReadOnly) delete prev.prices[taxPT];
        if (Object.keys(prev.prices).length === 0) pendingRef.current.delete(key);
        onEdited();
      }
    };
    if (text.trim() === "") { clearStaged(); return; }
    if (!derived) return;
    // Unchanged from what the cell showed on mount (focus+blur, or re-typing the
    // same value/rate) → don't create a phantom staged entry. This is the reliable
    // guard: it doesn't depend on the (possibly hidden/unfetched) tax column.
    const vNow = parseLoose(text), vInit = parseLoose(initialTextRef.current);
    const rNow = parseLoose(rateText), rInit = parseLoose(initialRateRef.current);
    if (
      vNow != null && vInit != null && Math.abs(vNow - vInit) < EPS &&
      rNow != null && rInit != null && Math.abs(rNow - rInit) < 1e-6
    ) {
      return;
    }
    // Reverted to the original input value (and rate) → drop any staged change.
    // Uses the baseline captured from the server data, so it works even after the
    // cell has remounted post-stage (and without re-reading the hidden tax column).
    if (
      baselineInput != null && vNow != null && Math.abs(vNow - baselineInput) < EPS &&
      (baselineRate == null || (rNow != null && Math.abs(rNow - baselineRate) < 0.01))
    ) {
      clearStaged();
      return;
    }
    // Edited to match the original server values → clear any prior staged change.
    if (rateReadOnly) {
      // Only pricePT is owned here; match on the net price alone (rate isn't editable).
      if (origPrice != null && Math.abs(derived.price - origPrice) < EPS) { clearStaged(); return; }
    } else if (taxIsRate) {
      const rNow2 = parseLoose(rateText);
      if (
        origPrice != null && origTax != null &&
        Math.abs(derived.price - origPrice) < EPS &&
        rNow2 != null && Math.abs(rNow2 - origTax) < 0.01
      ) { clearStaged(); return; }
    } else if (
      origPrice != null && origTax != null &&
      Math.abs(derived.price - origPrice) < EPS &&
      Math.abs(derived.tax - origTax) < EPS
    ) {
      clearStaged();
      return;
    }
    const row: PendingRow = prev ?? { scope, prices: {}, rawScopeParts };
    row.prices[pricePT] = derived.price;
    // rateReadOnly: store ONLY the net price (the rate is owned by a sibling column).
    // Rate-taxed: store the % rate verbatim. Otherwise store the derived tax amount.
    if (!rateReadOnly) row.prices[taxPT] = taxIsRate ? (parseLoose(rateText) ?? 0) : derived.tax;
    pendingRef.current.set(key, row);
    onEdited();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
  };

  // Tooltips. The whole-cell title overrides the grid's raw-value title (which
  // would otherwise show the stale underlying number on hover).
  const valueTitle =
    mode === "gross"
      ? "Total including tax. Net price and tax are computed from the rate."
      : "Net price (excluding tax). Tax is computed from the rate.";
  const rateTitle = "Tax rate for this row — defaults to the page rate; editing recalculates only this row.";
  const cellTitle = derived
    ? mode === "gross"
      ? `Total ${fmtPrice(derived.price + derived.tax)} incl. tax @ ${rateText} → net ${fmtPrice(derived.price)} + tax ${fmtPrice(derived.tax)}${staged ? " (unsaved)" : ""}`
      : `Net ${fmtPrice(derived.price)} @ ${rateText} → tax ${fmtPrice(derived.tax)}, total ${fmtPrice(derived.price + derived.tax)}${staged ? " (unsaved)" : ""}`
    : mode === "gross"
      ? "Enter the total including tax"
      : "Enter the net price";

  return (
    <span
      className={styles.calcCell}
      title={cellTitle}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <span className={styles.calcCellRow}>
        <input
          className={`${styles.calcCellInput}${staged ? ` ${styles.calcCellStaged}` : ""}`}
          type="text"
          inputMode="decimal"
          placeholder={mode === "gross" ? "total" : "net"}
          title={valueTitle}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={onKeyDown}
        />
        {rateReadOnly ? (
          <span className={styles.calcRateRO} title="Taxed at the Rental Tax Rate — edit it in the Rental column.">
            {rateText}
          </span>
        ) : (
          <input
            className={styles.calcRateInput}
            type="text"
            inputMode="decimal"
            title={rateTitle}
            placeholder="rate"
            value={rateText}
            onChange={(e) => setRateText(e.target.value)}
            onBlur={commit}
            onKeyDown={onKeyDown}
          />
        )}
      </span>
      <span
        className={styles.calcCellMeta}
        title={mode === "gross" ? "Computed net price and tax" : "Computed tax and total (incl. tax)"}
      >
        {derived
          ? mode === "gross"
            ? `net ${fmtPrice(derived.price)} · tax ${fmtPrice(derived.tax)}`
            : `tax ${fmtPrice(derived.tax)} · tot ${fmtPrice(derived.price + derived.tax)}`
          : "—"}
      </span>
    </span>
  );
}

/**
 * Build a GridOverride that buffers price-cell edits into `pendingRef` instead
 * of committing per-cell, and paints pending values + change highlight + the ▲
 * "higher override exists" mark + the composed "Scope" cell.
 */
export function buildPriceGridOverride(
  pendingRef: { current: PendingEdits },
  onEdited: () => void,
  scopeMaps: ScopeMaps,
  productMap: Map<number, string>,
  priceMode: PriceTaxMode = "direct",
  priceRate = "",
  hideLateTaxRate = false,
): GridOverride {
  const priceCols = new Set(PRICE_COLUMNS.map((c) => c.toLowerCase()));
  const calc = priceMode !== "direct";
  // lowercase price column → its pair (the calc-eligible price/tax columns). When the
  // Late Taxrate is hidden, the Reserve-Late pair is dropped so PT7 stays a direct column.
  const pairs = calcTaxPairs(hideLateTaxRate);
  const calcPriceCols = new Map<string, PriceTaxPair>(
    pairs.map((p) => [p.priceCol.toLowerCase(), p]),
  );

  // Original input value (+ rate) per scope/pair, captured from the row's SERVER
  // data the first time the cell renders. Survives cell remounts (lives in this
  // override closure) so a revert-to-original clears the staged change even when
  // the hidden tax column isn't re-read at commit time. Reset when the override is
  // rebuilt (mode/rate change), which is fine — it's re-captured on next render.
  const baseline = new Map<string, { input: number; rate: number | null }>();

  // Read one numeric cell by column name; NULL/blank/non-numeric → null.
  const readNum = (rowData: unknown[], colIndex: Record<string, number>, colName: string): number | null => {
    for (const k of Object.keys(colIndex)) {
      if (k.toLowerCase() === colName.toLowerCase()) {
        const raw = rowData[colIndex[k]!];
        if (raw == null || String(raw).trim() === "") return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      }
    }
    return null;
  };

  // ── Row removal (buffered, like edits) ─────────────────────────────────
  // Price types present in a grid row (price columns with a non-empty value).
  const rowPriceTypes = (rowData: unknown[], colIndex: Record<string, number>): number[] => {
    const out: number[] = [];
    for (const col of PRICE_COLUMNS) {
      for (const k of Object.keys(colIndex)) {
        if (k.toLowerCase() === col.toLowerCase()) {
          const raw = rowData[colIndex[k]!];
          if (raw != null && String(raw).trim() !== "") out.push(PRICE_COLUMN_TO_TYPE[col]!);
          break;
        }
      }
    }
    return out;
  };
  // Types a removal nulls out: those present in the row PLUS any already-pending
  // edits for the same scope (so removing also discards staged edits).
  const removalTypes = (prev: PendingRow | undefined, rowData: unknown[], colIndex: Record<string, number>): number[] => {
    const set = new Set<number>(rowPriceTypes(rowData, colIndex));
    if (prev) for (const pt of Object.keys(prev.prices)) set.add(Number(pt));
    return [...set];
  };
  const isRowRemoved = (prev: PendingRow | undefined, types: number[]): boolean =>
    !!prev && types.length > 0 && types.every((pt) => prev.prices[pt] === null);
  // Toggle: stage a removal (null every price type) or undo it (drop the entry).
  const toggleRemove = (scope: PriceScope, rowData: unknown[], colIndex: Record<string, number>): void => {
    const key = scopeKey(scope);
    const prev = pendingRef.current.get(key);
    const types = removalTypes(prev, rowData, colIndex);
    if (isRowRemoved(prev, types)) {
      pendingRef.current.delete(key);
    } else {
      const row: PendingRow = prev ?? { scope, prices: {}, rawScopeParts: rawScopePartsFromRow(rowData, colIndex, scopeMaps) };
      for (const pt of types) row.prices[pt] = null;
      pendingRef.current.set(key, row);
    }
    onEdited();
  };

  const cellDecorators: NonNullable<GridOverride["cellDecorators"]> = {};
  for (const col of PRICE_COLUMNS) {
    const calcPair = calcPriceCols.get(col.toLowerCase());
    cellDecorators[col] = (node: ReactNode, ctx) => {
      const scope = scopeFromRow(ctx.rowData, ctx.colIndex);
      const key = scopeKey(scope);
      const pending = pendingRef.current.get(key);
      const pt = PRICE_COLUMN_TO_TYPE[col]!;

      // Calc mode: the price cell becomes a net/gross editor; the paired tax
      // column is hidden by the page. (Non-paired columns keep normal behaviour.)
      if (calc && calcPair) {
        const taxPT = calcPair.taxType;
        const stagedPrice = pending && pt in pending.prices ? pending.prices[pt] : undefined;
        const stagedTax = pending && taxPT in pending.prices ? pending.prices[taxPT] : undefined;
        const origPrice = readNum(ctx.rowData, ctx.colIndex, calcPair.priceCol);
        const origTax = readNum(ctx.rowData, ctx.colIndex, calcPair.taxCol);
        const curPrice = stagedPrice !== undefined ? stagedPrice : origPrice;
        const curTax = stagedTax !== undefined ? stagedTax : origTax;
        const staged = stagedPrice !== undefined || stagedTax !== undefined;

        // Capture the original input value (+rate) once, from the server data, so a
        // revert-to-original can clear the stage regardless of later cell remounts.
        const taxIsRate = !!calcPair.taxIsRate;
        const bkey = `${key}:${pt}`;
        const origInput = priceMode === "gross"
          ? (taxIsRate
              ? (origPrice != null ? origPrice * (1 + (origTax ?? 0) / 100) : null)
              : (origPrice != null && origTax != null ? origPrice + origTax : origPrice))
          : origPrice;
        // For a rate-taxed column the baseline rate IS the stored rate (origTax);
        // for a flat-tax pair it's the price/tax effective rate.
        const origRate = taxIsRate
          ? origTax
          : (origPrice != null && origTax != null && origPrice !== 0 ? effectiveRate(origPrice, origTax) : null);
        if (origInput != null && !baseline.has(bkey)) baseline.set(bkey, { input: origInput, rate: origRate });
        const baseRec = baseline.get(bkey);

        return (
          <CalcPriceCell
            key={`calc-${pt}-${priceMode}-${priceRate}-${staged ? "s" : "o"}-${curPrice ?? ""}-${curTax ?? ""}`}
            mode={priceMode}
            rate={priceRate}
            pricePT={pt}
            taxPT={taxPT}
            taxIsRate={taxIsRate}
            scope={scope}
            rawScopeParts={rawScopePartsFromRow(ctx.rowData, ctx.colIndex, scopeMaps)}
            curPrice={curPrice}
            curTax={curTax}
            origPrice={origPrice}
            origTax={origTax}
            baselineInput={baseRec?.input ?? null}
            baselineRate={baseRec?.rate ?? null}
            staged={staged}
            pendingRef={pendingRef}
            onEdited={onEdited}
          />
        );
      }

      // Reserve Period (PT9) is taxed at the Rental Tax Rate (PT4), which the Rental
      // cell owns. In calc mode fold it into a net/gross editor that READS that rate
      // (read-only — edit it in the Rental column) and stores ONLY PT9, so it respects
      // Net/Gross like every other price column without fighting over the shared rate.
      if (pt === 9) {
        const title = "Net period price — taxed at the Rental Tax Rate (the Rental column / PT4), like the editor's Reserve group. If left blank, the kiosk falls back to the Rental Price.";
        if (calc) {
          const stagedPrice = pending && 9 in pending.prices ? pending.prices[9] : undefined;
          const stagedRate = pending && 4 in pending.prices ? pending.prices[4] : undefined;
          const origPeriod = readNum(ctx.rowData, ctx.colIndex, "ReservePeriodPrice");
          const ratev = stagedRate !== undefined ? stagedRate : readNum(ctx.rowData, ctx.colIndex, "RentalTax");
          const curPeriod = stagedPrice !== undefined ? stagedPrice : origPeriod;
          const stagedP = stagedPrice !== undefined;
          const bkey = `${key}:9`;
          const origInput = priceMode === "gross"
            ? (origPeriod != null ? origPeriod * (1 + (ratev ?? 0) / 100) : null)
            : origPeriod;
          if (origInput != null && !baseline.has(bkey)) baseline.set(bkey, { input: origInput, rate: ratev });
          const baseRec = baseline.get(bkey);
          return (
            <CalcPriceCell
              key={`calc-9-${priceMode}-${priceRate}-${stagedP ? "s" : "o"}-${curPeriod ?? ""}-${ratev ?? ""}`}
              mode={priceMode}
              rate={priceRate}
              pricePT={9}
              taxPT={4}
              taxIsRate
              rateReadOnly
              scope={scope}
              rawScopeParts={rawScopePartsFromRow(ctx.rowData, ctx.colIndex, scopeMaps)}
              curPrice={curPeriod}
              curTax={ratev}
              origPrice={origPeriod}
              origTax={ratev}
              baselineInput={baseRec?.input ?? null}
              baselineRate={baseRec?.rate ?? null}
              staged={stagedP}
              pendingRef={pendingRef}
              onEdited={onEdited}
            />
          );
        }
        if (pending && 9 in pending.prices) {
          const v = pending.prices[9];
          return <span title={title} className={v === null ? styles.cellDeleted : styles.cellChanged}>{v === null ? "—" : fmtPrice(v as number)}</span>;
        }
        return <span title={title}>{node}</span>;
      }

      if (!pending || !(pt in pending.prices)) return node;
      const v = pending.prices[pt];
      const deleted = v === null;
      return (
        <span className={deleted ? styles.cellDeleted : styles.cellChanged}>
          {deleted ? "—" : fmtPrice(v as number)}
        </span>
      );
    };
  }
  // Product column: compose "#{id} {name} (extItem)" for display, plus the ▲
  // mark when a higher-weight override exists for this product. The underlying
  // value stays the numeric Product id (FilterProduct / iProductID|INT / scope
  // logic unaffected) — this is display-only. The external item id (ProductRef)
  // is folded in here, so its standalone "Ext Item" column is hidden by the page.
  cellDecorators["Product"] = (_node: ReactNode, ctx) => {
    const findIdx = (name: string): number | undefined => {
      for (const k of Object.keys(ctx.colIndex)) {
        if (k.toLowerCase() === name.toLowerCase()) return ctx.colIndex[k];
      }
      return undefined;
    };
    const num = (name: string): number => {
      const i = findIdx(name);
      if (i == null) return 0;
      const raw = ctx.rowData[i];
      const n = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
      return Number.isFinite(n) ? n : 0;
    };
    const str = (name: string): string => {
      const i = findIdx(name);
      if (i == null) return "";
      const raw = ctx.rowData[i];
      return raw == null ? "" : String(raw).trim();
    };

    const id = num("Product");
    const name = productMap.get(id) ?? "";
    const ext = str("ProductRef");
    // Only flag a higher-priority override when BOTH columns are actually present
    // in the grid data. If the page-def drops "Weight" (or ProductMaxWeight), num()
    // would read 0 and ProductMaxWeight > 0 would paint a false ▲ on every row.
    const hasWeights = findIdx("ProductMaxWeight") != null && findIdx("Weight") != null;
    const higher = hasWeights && num("ProductMaxWeight") > num("Weight");

    const scope = scopeFromRow(ctx.rowData, ctx.colIndex);
    const prev = pendingRef.current.get(scopeKey(scope));
    const removed = isRowRemoved(prev, removalTypes(prev, ctx.rowData, ctx.colIndex));
    return (
      <span className={removed ? styles.productRemoved : undefined}>
        <button
          type="button"
          className={removed ? styles.undoRowBtn : styles.removeRowBtn}
          title={removed ? "Undo removal — keep this override" : "Remove this override (staged for Save All)"}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleRemove(scope, ctx.rowData, ctx.colIndex); }}
        >
          {removed ? "↶" : "✕"}
        </button>
        <span className={styles.productId}>#{id}</span>
        {name && <span className={styles.productName}>{name}</span>}
        {ext && <span className={styles.extItem}>({ext})</span>}
        {higher && <span className={styles.higherMark} title="A higher-priority override exists for this product">▲</span>}
      </span>
    );
  };

  // "Scope" column: compose the classic scope label (Operator/State/Chain/Location/
  // Kiosk, name-resolved via cache maps) from the row's scope siblings. Attached to
  // SCOPE_COLUMN, whose header the page relabels to "Scope" via extraColumnInfo.
  cellDecorators[SCOPE_COLUMN] = (_node: ReactNode, ctx) => {
    // Read RAW cells so NULL (unset) and 0 (a specified scope value) stay
    // distinct — 0 must be shown, only NULL is hidden.
    const parts = rawScopePartsFromRow(ctx.rowData, ctx.colIndex, scopeMaps);
    if (parts.length === 0) return <span className={styles.scopeDefault}>Product Default</span>;
    const full = parts.map((p) => `${p.kind}: ${p.value}`).join(" + ");
    return <ScopeCell parts={parts} full={full} />;
  };

  // In calc mode the price columns are driven by our own input (CalcPriceCell),
  // so lock the grid's native inline editor on them; the tax columns are hidden.
  const cellLocked: NonNullable<GridOverride["cellLocked"]> = {};
  if (calc) {
    for (const p of pairs) cellLocked[p.priceCol] = () => true;
    cellLocked["ReservePeriodPrice"] = () => true; // driven by its read-only-rate calc cell
  }

  return {
    cellDecorators,
    cellLocked,
    onCellEditCommit: (ctx) => {
      if (!priceCols.has(ctx.colName.toLowerCase())) return { kind: "default" };
      const colKey = Object.keys(PRICE_COLUMN_TO_TYPE).find((c) => c.toLowerCase() === ctx.colName.toLowerCase())!;
      const pt = PRICE_COLUMN_TO_TYPE[colKey]!;
      const scope = scopeFromRow(ctx.rowData, ctx.colIndex);
      const key = scopeKey(scope);
      const raw = String(ctx.newValue ?? "").trim();
      const value: number | null = raw === "" ? null : Number(raw);
      if (value !== null && !Number.isFinite(value)) return { kind: "skip" };

      // Compare to the ORIGINAL server value: a no-op edit (re-typing the existing
      // value, or reverting a pending change) must not create/keep a pending entry,
      // so it never shows under "Changes Made".
      const origIdx = ctx.colIndex[ctx.colName];
      const origRaw = origIdx != null ? ctx.rowData[origIdx] : undefined;
      const origStr = origRaw == null ? "" : String(origRaw).trim();
      const orig: number | null = origStr === "" ? null : Number(origStr);
      const unchanged =
        (value === null && orig === null) ||
        (value !== null && orig !== null && Math.abs(value - orig) < EPS);

      const prev = pendingRef.current.get(key);
      if (unchanged) {
        if (prev && pt in prev.prices) {
          delete prev.prices[pt];
          if (Object.keys(prev.prices).length === 0) pendingRef.current.delete(key);
        }
      } else {
        const row: PendingRow = prev ?? { scope, prices: {}, rawScopeParts: rawScopePartsFromRow(ctx.rowData, ctx.colIndex, scopeMaps) };
        row.prices[pt] = value;
        pendingRef.current.set(key, row);
      }
      onEdited();
      return { kind: "local", value: value === null ? "" : value };
    },
  };
}
